import { useMemo, useCallback } from 'react';
import { EmptyState } from './components/empty-state';
import { InitialState } from './components/initial-state';
import { SearchLoadingSkeleton } from './components/search-loading-skeleton';
import { RecentSearchRecords } from './search-suggestions/user-recent-search/recent-search-records';
import { useRecentSearchRecords } from './search-suggestions/user-recent-search/use-recent-search-records.query';
import { useSearchResults } from './use-search-results.query';
import { useSearchResultsCounts } from './use-search-results-counts.query';
import type { SearchCountsQuery } from '@/api/v4/search.api';

import 'react-loading-skeleton/dist/skeleton.css';
import { SearchResultsContent } from './search-results-content';
import { searchQueryState, searchResultsFilterState } from './search.state';
import { useAtom, useAtomValue } from 'jotai';

export const SearchContent = () => {
  const [searchQuery, setSearchQuery] = useAtom(searchQueryState);
  const selectedFilter = useAtomValue(searchResultsFilterState);

  const {
    data: searchResults,
    isFetching: isFetchingSearchResults,
    isLoading: isLoadingSearchResults,
  } = useSearchResults(searchQuery, { activeFilter: selectedFilter });

  const countsQuery = useMemo<SearchCountsQuery>(
    () => ({ search: searchQuery.search }),
    [searchQuery.search],
  );

  const { data: searchCounts, isLoading: isLoadingCounts } =
    useSearchResultsCounts(countsQuery);

  const {
    hasUserSomeRecentSearchRecords,
    data: recentSearchRecords,
    isLoading: isLoadingRecentSearchRecords,
  } = useRecentSearchRecords();

  const isLoading =
    isFetchingSearchResults || isLoadingRecentSearchRecords || isLoadingCounts;
  const results = searchResults?.results ?? [];
  const hasResults = results.length > 0;

  const recentRecords = useMemo(
    () => recentSearchRecords ?? [],
    [recentSearchRecords],
  );

  const hasNextPage = searchResults
    ? searchResults.results.length < searchResults.total
    : false;

  // Load more results (pagination)
  const doShowMore = useCallback(() => {
    setSearchQuery(prev => ({
      ...prev,
      page: (prev.page ?? 1) + 1,
    }));
  }, [setSearchQuery]);

  // Render results content if there are results OR when a specific tab is selected.
  // Only hide results UI when 'all' is selected and there are no results.
  if (selectedFilter !== 'all' || hasResults) {
    // Only use props that match the SearchResultsContent interface
    return (
      <SearchResultsContent
        results={results}
        hasNextPage={hasNextPage}
        onShowMore={doShowMore}
        isLoading={isLoadingSearchResults || isFetchingSearchResults}
        totalResults={searchCounts?.total ?? searchResults.total}
        counts={searchCounts ?? undefined}
      />
    );
  }

  const searchPhrase = searchQuery.search;

  if (isLoading) {
    return <SearchLoadingSkeleton columns={6} />;
  }

  if (searchPhrase && !hasResults) {
    return <EmptyState searchPhrase={searchPhrase} />;
  }

  if (hasUserSomeRecentSearchRecords && !searchPhrase) {
    return <RecentSearchRecords records={recentRecords} />;
  }

  if (!searchPhrase) {
    return <InitialState />;
  }

  return null;
};
